import { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions, db, auth } from '@/config/firebase';
import { ISLAND_COST, ISLAND_COST_BUINK } from '../constants';
import '../styles/Market.css';
import { collection, query, where, orderBy, limit, getDocs, startAfter, QueryDocumentSnapshot, DocumentData } from 'firebase/firestore';
import { useUser } from '@/context/UserContext';
import { FlyingIsland } from '@/config/flyingIslands';

const purchaseVoxelPackCallable = httpsCallable(functions, 'purchaseVoxelPack');
const purchaseItemWithVirtualWalletCallable = httpsCallable(functions, 'purchaseItemWithVirtualWallet');
const getMarketSettingsCallable = httpsCallable(functions, 'getMarketSettings');
const BUINK_PER_TON = 10;

// Constants
const PAGE_SIZE = 6;
const defaultPointPacks = [
    { amount: 10, price: 1.0 },
    { amount: 20, price: 2.0 },
    { amount: 50, price: 5.0 },
    { amount: 100, price: 10.0 },
    { amount: 1000, price: 100.0 },
];
const defaultVoxelPacks = [
    { size: 10, price: 50, name: "Small Pack" },
    { size: 50, price: 250, name: "Medium Pack" },
    { size: 100, price: 500, name: "Large Pack" },
];

const Market = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
    const { user, updateIslands } = useUser();

    const [activeTab, setActiveTab] = useState<'islands' | 'voxels' | 'points' | 'chad'>('islands');
    const [loading, setLoading] = useState(false);
    const [pointPacks, setPointPacks] = useState(defaultPointPacks);
    const [voxelPacks, setVoxelPacks] = useState(defaultVoxelPacks);
    const [islandCostTon, setIslandCostTon] = useState(ISLAND_COST);
    const [islandCostBuink, setIslandCostBuink] = useState(ISLAND_COST_BUINK);

    // Island Data State
    const [islands, setIslands] = useState<({ id: string } & FlyingIsland)[]>([]);
    const [lastDoc, setLastDoc] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
    const [loadingIslands, setLoadingIslands] = useState(false);
    const [hasMoreIslands, setHasMoreIslands] = useState(true);

    const fetchIslands = async () => {
        if (loadingIslands || !hasMoreIslands) return;
        setLoadingIslands(true);
        try {
            let q = query(
                collection(db, 'flyingIslands'),
                where('owner', '==', ''),
                orderBy('coords.x'),
                limit(PAGE_SIZE)
            );

            if (lastDoc) {
                q = query(
                    collection(db, 'flyingIslands'),
                    where('owner', '==', ''),
                    orderBy('coords.x'),
                    startAfter(lastDoc),
                    limit(PAGE_SIZE)
                );
            }

            const snapshot = await getDocs(q);
            const newIslands = snapshot.docs.map(doc => ({
                id: doc.id,
                ...(doc.data() as FlyingIsland)
            }));

            setIslands(prev => [...prev, ...newIslands]);
            setLastDoc(snapshot.docs[snapshot.docs.length - 1] || null);
            setHasMoreIslands(snapshot.docs.length === PAGE_SIZE);

        } catch (error) {
            console.error('[Market] Failed to fetch islands:', error);
        } finally {
            setLoadingIslands(false);
        }
    };

    // Reset island state when tab changes
    useEffect(() => {
        if (activeTab === 'islands') {
            setIslands([]);
            setLastDoc(null);
            setHasMoreIslands(true);
            fetchIslands();
        }
    }, [activeTab]);

    useEffect(() => {
        const loadMarketSettings = async () => {
            try {
                const res: any = await getMarketSettingsCallable();
                setPointPacks((res.data?.pointPacks || defaultPointPacks) as typeof defaultPointPacks);
                setVoxelPacks((res.data?.voxelPacks || defaultVoxelPacks) as typeof defaultVoxelPacks);
                setIslandCostTon(Number(res.data?.islandCostTon) || ISLAND_COST);
                setIslandCostBuink(Number(res.data?.islandCostBuink) || ISLAND_COST_BUINK);
            } catch (error) {
                console.error("[Market] Failed to load market settings:", error);
            }
        };
        void loadMarketSettings();
    }, []);


    if (!isOpen) return null;

    const buyIsland = async (islandId: string) => {
        setLoading(true);
        try {
            if (!auth.currentUser) return alert("Authentication not ready. Please try again in a moment.");
            if (!user?.walletId) return alert("Virtual wallet not found. Please log in again.");

            const response = await purchaseItemWithVirtualWalletCallable({
                itemId: islandId,
                itemType: 'island',
                currency: 'ton'
            });

            if ((response.data as any).success) {
                alert("Island purchased with TON!");
                updateIslands();
                setIslands(prev => prev.filter(i => i.id !== islandId));
            }
        } catch (e: any) {
            console.error(e);
            alert("Failed: " + e.message);
        } finally {
            setLoading(false);
        }
    };

    const buyIslandWithBuink = async (islandId: string) => {
        setLoading(true);
        try {
            const response = await purchaseItemWithVirtualWalletCallable({
                itemId: islandId,
                itemType: 'island',
                currency: 'buink'
            });

            if ((response.data as any).success) {
                alert("Island purchased with Buink!");
                updateIslands();
                setIslands(prev => prev.filter(i => i.id !== islandId));
            }
        } catch (e: any) {
            console.error(e);
            alert("Failed: " + e.message);
        } finally {
            setLoading(false);
        }
    };

    const buyPoints = async (packIndex: number, currency: 'ton' | 'buink' = 'ton') => {
        setLoading(true);
        const pack = pointPacks[packIndex];
        try {
            if (currency === 'ton') {
                if (!user?.walletId) return alert("Virtual wallet not found. Please log in again.");
                const response = await purchaseItemWithVirtualWalletCallable({
                    packIndex: packIndex,
                    itemType: 'points',
                    currency: 'ton'
                });
                if ((response.data as any).success) {
                    alert(`Purchased ${pack.amount} points with TON!`);
                }
            } else {
                // Buink Purchase
                const response = await purchaseItemWithVirtualWalletCallable({
                    packIndex: packIndex,
                    itemType: 'points',
                    currency: 'buink'
                });
                if ((response.data as any).success) {
                    alert(`Purchased ${pack.amount} points with Buink!`);
                }
            }
        } catch (e: any) {
            console.error(e);
            alert("Failed: " + e.message);
        } finally {
            setLoading(false);
        }
    };

    const buyVoxels = async (packIndex: number, pack: typeof voxelPacks[0]) => {
        if (!user || (user.points || 0) < pack.price) return alert("Not enough points.");
        setLoading(true);
        try {
            const res = await purchaseVoxelPackCallable({ packIndex });
            if ((res.data as any).success) alert(`Purchased ${pack.size} blocks!`);
        } catch (e: any) {
            console.error(e);
            alert("Failed: " + e.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="market-overlay">
            <div className="market-head">
                <button className="close-button" onClick={onClose}>✖</button>
                <h2>Marketplace</h2>

                <div className="market-tabs">
                    <button onClick={() => setActiveTab('islands')} className={activeTab === 'islands' ? 'active' : ''}>🪐 Islands</button>
                    <button onClick={() => setActiveTab('voxels')} className={activeTab === 'voxels' ? 'active' : ''}>🧱 Voxels</button>
                    <button onClick={() => setActiveTab('points')} className={activeTab === 'points' ? 'active' : ''}>🎯 Points</button>
                    <button onClick={() => setActiveTab('chad')} className={activeTab === 'chad' ? 'active' : ''}>🦍 CHAD</button>
                </div>

                <div className="market-container">

                    {/* ISLANDS TAB */}
                    {activeTab === 'islands' && (
                        <>
                            <div className="item-grid">
                                {islands.map((island) => (
                                    <div key={island.id} className="item-card">
                                        <img src={'/flyingIsland.jpeg'} alt={`Island ${island.id}`} />
                                        <h3>Island {island.id.substring(0, 6)}</h3>
                                        {/* <p>Price: {ISLAND_COST} TON</p>
                                        <p>or {ISLAND_COST_BUINK} Buink</p> */}
                                        <div className="purchase-buttons">
                                            <button onClick={() => buyIsland(island.id)} disabled={loading}>
                                                {loading ? "..." : `Buy (${islandCostTon} TON)`}
                                            </button>
                                            <button onClick={() => buyIslandWithBuink(island.id)} disabled={loading} className="buink-btn">
                                                {loading ? "..." : `Buy (${islandCostBuink} Buink)`}
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="market-footer">
                                {loadingIslands && <p>Loading...</p>}
                                {!loadingIslands && hasMoreIslands && (
                                    <button className="load-more-button" onClick={fetchIslands}>Load More</button>
                                )}
                                {!loadingIslands && islands.length === 0 && <p>No islands available.</p>}
                            </div>
                        </>
                    )}

                    {/* VOXELS TAB */}
                    {activeTab === 'voxels' && (
                        <div className="item-grid">
                            {voxelPacks.map((pack, i) => (
                                <div key={i} className="item-card">
                                    <img src={'/blocks.png'} alt="Blocks" />
                                    <h3>{pack.name}</h3>
                                    <p>{pack.size} Blocks</p>
                                    <button onClick={() => buyVoxels(i, pack)} disabled={loading}>
                                        Buy ({pack.price} Points)
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* POINTS TAB */}
                    {activeTab === 'points' && (
                        <div className="item-grid">
                            {pointPacks.map((pack, i) => (
                                <div key={i} className="item-card">
                                    <img src={'/point.webp'} alt="Points" />
                                    <h3>{pack.amount} Points</h3>
                                    <p>{pack.price} TON</p>
                                    <div className="purchase-buttons">
                                        <button onClick={() => buyPoints(i, 'ton')} disabled={loading}>
                                            {loading ? "..." : `Buy (${pack.price} TON)`}
                                        </button>
                                        <button onClick={() => buyPoints(i, 'buink')} disabled={loading} className="buink-btn">
                                            {loading ? "..." : `Buy (${Math.ceil(Number(pack.price || 0) * BUINK_PER_TON)} Buink)`}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* CHAD TAB */}
                    {activeTab === 'chad' && (
                        <div style={{ textAlign: 'center', padding: '20px' }}>
                            <h3>Chad Market Coming Soon!</h3>
                            <p>Exclusive items for the ultimate chads.</p>
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
};

export default Market;
